import { REELS_CREATOR_TOOL_ID } from '@/shared/utils/reels-creator';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@monksflow/monks-ui';
import { ClapperboardIcon, SettingsIcon, SwatchBookIcon } from 'lucide-react';

import { useIsReelsAdmin } from '@/shared/config/useIsReelsAdmin';

import { ROUTES } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderActions,
  PageHeaderIdentity,
  PageHeaderTitle,
  PageHeaderToolGroup,
} from '@/shared/ui/layout';

/**
 * The sessions home's header and body frame. The route renders it once and swaps only its
 * children (list skeleton, then the list), so the header never remounts. This module stays
 * out of the lazy page's dependencies: it renders from the entry chunk.
 */
export const ReelsCreatorHomeFrame = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('reelsCreator');
  const navigate = useNavigate();
  const isReelsAdmin = useIsReelsAdmin();

  return (
    <div data-testid="reels-creator-home" className="page-layout">
      <ListPageLayout>
        <StaggerItem index={0}>
          <PageHeader>
            <PageHeaderIdentity>
              <PageHeaderToolGroup
                toolId={REELS_CREATOR_TOOL_ID}
                category="create"
                icon={ClapperboardIcon}
              >
                <PageHeaderTitle>{t('reelsCreatorHomePage.title')}</PageHeaderTitle>
              </PageHeaderToolGroup>
            </PageHeaderIdentity>
            <PageHeaderActions>
              {isReelsAdmin ? (
                <Button
                  variant="outline"
                  onClick={() => void navigate(ROUTES.REELS_CREATOR_SETTINGS)}
                >
                  <SettingsIcon />
                  {t('reelsCreatorHomePage.settings')}
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => void navigate(ROUTES.REELS_CREATOR_BRANDS)}>
                <SwatchBookIcon />
                {t('reelsCreatorHomePage.brandKits')}
              </Button>
            </PageHeaderActions>
          </PageHeader>
        </StaggerItem>

        <ListPageBody>
          <StaggerItem index={3} className="flex min-h-0 flex-1 flex-col">
            {children}
          </StaggerItem>
        </ListPageBody>
      </ListPageLayout>
    </div>
  );
};
